import {useCallback, useEffect, useRef, useState} from 'react'

import {
  DESKTOP_ARROW_STEP_COLS,
  DESKTOP_GRID_GAP,
  DESKTOP_GRID_ROWS,
  DESKTOP_PRELOAD_PX,
  DESKTOP_SCROLL_STEP,
  DESKTOP_THUMB_COL_WIDTH
} from './config.js'

const computeVisibleCells = ({scrollLeft, clientWidth, totalItems}) => {
  const featuredWidth = DESKTOP_SCROLL_STEP
  const thumbColWidth = DESKTOP_THUMB_COL_WIDTH + DESKTOP_GRID_GAP
  const visibleRight = scrollLeft + clientWidth + DESKTOP_PRELOAD_PX

  const next = new Set([0])
  let colLeft = featuredWidth + DESKTOP_GRID_GAP
  let i = 1
  while (colLeft <= visibleRight && i < totalItems) {
    for (let row = 0; row < DESKTOP_GRID_ROWS; row++) next.add(i + row)
    i += DESKTOP_GRID_ROWS
    colLeft += thumbColWidth
  }
  return next
}

const isSameCellSet = (a, b) => {
  if (a.size !== b.size) return false
  for (const value of a) {
    if (!b.has(value)) return false
  }
  return true
}

const isLastRealItemVisible = (el, realItems) => {
  const lastRealCell = el.children[realItems - 1]
  if (!lastRealCell) return false
  return lastRealCell.getBoundingClientRect().right <= el.getBoundingClientRect().right + 1
}

const getScrollState = (el, realItems) => ({
  atStart: el.scrollLeft <= 0,
  atEnd: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1 || isLastRealItemVisible(el, realItems)
})

export default function useDesktopGalleryScroll(totalItems, realItems) {
  const scrollRef = useRef(null)
  const firstThumbRef = useRef(null)
  const featuredRef = useRef(null)
  const rafRef = useRef(null)

  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)
  const [visibleCells, setVisibleCells] = useState(() => new Set([0]))
  const [isFeaturedVisible, setIsFeaturedVisible] = useState(true)

  const syncState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const {atStart: nextStart, atEnd: nextEnd} = getScrollState(el, realItems)
    setAtStart(nextStart)
    setAtEnd(nextEnd)

    // visibleCells is a preload set — it only grows. isFeaturedVisible tracks whether the
    // featured cell is actually on screen, which is what the inline video player checks.
    const featured = featuredRef.current
    if (featured !== null) {
      const cell = featured.getBoundingClientRect()
      const track = el.getBoundingClientRect()
      setIsFeaturedVisible(cell.right > track.left && cell.left < track.right)
    }
    setVisibleCells(prev => {
      const next = computeVisibleCells({
        scrollLeft: el.scrollLeft,
        clientWidth: el.clientWidth,
        totalItems
      })
      return isSameCellSet(next, prev) ? prev : next
    })
  }, [totalItems, realItems])

  const handleScroll = useCallback(() => {
    if (rafRef.current !== null) return
    rafRef.current = requestAnimationFrame(() => {
      syncState()
      rafRef.current = null
    })
  }, [syncState])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    syncState()
    el.addEventListener('scroll', handleScroll, {passive: true})

    const ro = new ResizeObserver(syncState)
    ro.observe(el)

    return () => {
      el.removeEventListener('scroll', handleScroll)
      ro.disconnect()
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [syncState, handleScroll])

  const getStep = useCallback(() => {
    if (!firstThumbRef.current) return DESKTOP_SCROLL_STEP
    return DESKTOP_ARROW_STEP_COLS * (firstThumbRef.current.offsetWidth + DESKTOP_GRID_GAP)
  }, [])

  const scrollPrev = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const step = getStep()
    el.scrollTo({left: Math.max(0, Math.round(el.scrollLeft / step) * step - step), behavior: 'smooth'})
  }, [getStep])

  const scrollNext = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const step = getStep()
    el.scrollTo({left: Math.round(el.scrollLeft / step) * step + step, behavior: 'smooth'})
  }, [getStep])

  return {
    scrollRef,
    firstThumbRef,
    featuredRef,
    atStart,
    atEnd,
    visibleCells,
    isFeaturedVisible,
    scrollPrev,
    scrollNext
  }
}
